<template v-slot:item="data">
    <q-card
        @click="$emit('select')"
        class="full-width q-mb-sm"
        :class="{
            'product-card--selectable': isSelectable,
            'product-card--selected': isSelected
        }"
        bordered
        flat
    >
        <q-card-section :horizontal="$q.screen.gt.xs" class="q-pa-none">
            <q-card-section class="col-xs-12 col-sm-10">
                <div class="row q-my-sm">
                    <div class="col-12">
                        <p class="text-h6 q-mb-xs ellipsis">
                            {{ name }}
                        </p>
                        <p
                            class="product-card__description q-mb-none wrap"
                            v-if="description.length > 0"
                        >
                            {{ description }}
                        </p>
                    </div>
                    <div class="col-12 q-mt-md">
                        <div class="row">
                            <!-- SKU -->
                            <div class="col-12 col-sm-6 product-card__icons">
                                <q-icon
                                    name="fa-solid fa-barcode-read"
                                    :size="$q.screen.lt.sm ? 'xs' : 'sm'"
                                    class="q-mr-md"
                                >
                                </q-icon>
                                <p class="q-mb-xs q-pr-md ellipsis">
                                    {{ code }}
                                </p>
                            </div>

                            <!-- Unit packing / measure-->
                            <div class="col-12 col-sm-6 product-card__icons">
                                <q-icon
                                    name="fa-regular fa-boxes-packing"
                                    :size="$q.screen.lt.sm ? 'xs' : 'sm'"
                                    class="q-mr-md"
                                >
                                </q-icon>
                                <p class="q-mb-xs">
                                    {{
                                        // Translators: {value} is the quantity value, {unit} is the quantity unit (e.g. kg).
                                        $t('{value} {unit}', {
                                            value: roundTo(
                                                convertedUnits?.packing,
                                                3
                                            ),
                                            unit: convertedUnits?.measure
                                        })
                                    }}
                                </p>
                            </div>
                        </div>

                        <q-separator
                            v-if="
                                form.runRates?.length > 0 &&
                                editablePackagingFactors.length > 0
                            "
                            class="q-my-md"
                        />
                    </div>

                    <div
                        v-if="form.runRates.length > 0"
                        class="col-sm-12 col-xs-12"
                    >
                        <!-- Max run rate -->
                        <div
                            class="row text-secondary align-items-center q-mt-md"
                        >
                            <p
                                class="text-weight-medium text-secondary q-mb-none q-mr-md"
                            >
                                {{ $t('Max run rate') }}
                            </p>
                            <product-expected-run-rates
                                :expectedRunRates="form.runRates"
                                :isEditMode="inlineEditMode"
                                @changedRunRates="
                                    rates => (form.runRates = rates)
                                "
                            />
                        </div>
                    </div>
                </div>

                <div
                    class="col-sm-12 col-xs-12 text-secondary"
                    v-if="editablePackagingFactors.length > 0"
                >
                    <p class="text-weight-medium q-mb-sm">
                        {{ $t('Packaging Factors') }}
                    </p>
                    <div
                        v-for="(factorValue, factor) in packagingFactors"
                        :key="factor"
                        :class="
                            editablePackagingFactors.includes(factor)
                                ? $q.screen.lt.sm
                                    ? 'col-12'
                                    : 'col-6'
                                : ' '
                        "
                    >
                        <div v-if="editablePackagingFactors.includes(factor)">
                            <!-- Packaging factors -->
                            <div v-if="inlineEditMode">
                                <q-input
                                    ref="sensorPackagingFactors"
                                    v-model.number="
                                        form.packagingFactors[factor]
                                    "
                                    :rules="formRules.packagingFactors"
                                    :label="
                                        $t(
                                            `${getSensorLabel(factor)} ` +
                                                'sensor packaging factor'
                                        )
                                    "
                                    :hint="
                                        $t(
                                            'Multiplication factor to apply on sensor'
                                        )
                                    "
                                    icon="far fa-sensor"
                                    outlined
                                    dense
                                    type="number"
                                    required
                                    class="q-my-sm"
                                    color="accent"
                                >
                                    <template v-slot:prepend>
                                        <q-icon name="far fa-sensor" />
                                    </template>
                                </q-input>
                            </div>
                            <div
                                v-if="factorValue && !inlineEditMode"
                                class="row q-pb-sm"
                            >
                                <div>
                                    {{
                                        this.$t('{sensor_type} Sensor', {
                                            sensor_type: getSensorLabel(factor)
                                        }) + ': '
                                    }}
                                </div>
                                <div class="col-xs-2 col-sm-2">
                                    {{ factorValue }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </q-card-section>

            <!-- Actions -->
            <q-separator :vertical="$q.screen.gt.xs" />
            <q-card-section
                class="col-2 flex flex-center"
                :class="$q.screen.lt.xs ? 'q-py-none' : 'q-px-none'"
            >
                <q-card-actions
                    class="col-auto justify-around q-pl-xs q-pr-none"
                    align="right"
                    vertical
                >
                    <q-card-actions
                        v-if="isSelectable"
                        class="col-auto justify-around q-pl-xs q-pr-none"
                        align="right"
                        vertical
                    >
                        <q-icon
                            :name="
                                isSelected
                                    ? 'fas fa-check-square'
                                    : 'fal fa-square'
                            "
                            color="primary"
                            size="md"
                        >
                        </q-icon>
                    </q-card-actions>
                    <q-card-actions
                        v-else
                        class="col-auto justify-around q-pl-xs full-width"
                        :class="$q.screen.lt.xs ? 'q-py-none' : ''"
                        :align="$q.screen.lt.xs ? 'right' : 'center'"
                        :vertical="$q.screen.gt.xs ? true : false"
                    >
                        <q-separator
                            vertical
                            v-if="$q.screen.gt.xs ? true : false"
                        ></q-separator>
                        <q-btn
                            v-if="inlineEditMode"
                            @click="save"
                            class="q-pa-sm q-mb-sm"
                            icon="fas fa-check"
                            color="positive"
                            text-color="white"
                            size="sm"
                        >
                            <q-tooltip>
                                {{ $t('Save product') }}
                            </q-tooltip>
                        </q-btn>
                        <q-btn
                            v-else
                            @click="$emit('edit')"
                            :disable="!canEdit"
                            :color="!canEdit ? 'grey' : 'accent'"
                            class="q-pa-sm card__button--edit"
                            icon="fas fa-edit"
                            size="md"
                            flat
                        />
                        <q-btn
                            v-if="inlineEditMode"
                            @click="$emit('cancel')"
                            class="q-pa-sm"
                            icon="fas fa-times"
                            color="negative"
                            size="sm"
                            outline
                        >
                            <q-tooltip>
                                {{ $t('Cancel') }}
                            </q-tooltip>
                        </q-btn>
                        <q-btn
                            v-else
                            @click="$emit('delete')"
                            :disable="!canDelete"
                            :color="!canDelete ? 'grey' : 'red-6'"
                            class="q-pa-sm card__button--delete"
                            icon="fas fa-trash"
                            size="md"
                            flat
                        />
                    </q-card-actions>
                </q-card-actions>
            </q-card-section>
        </q-card-section>
    </q-card>
</template>

<script>
import ProductExpectedRunRates from '@components/ProductExpectedRunRates.vue';
import { convertToUnitSystem } from '@models/products.js';
import { SENSOR_TYPES } from '@models/sensorTypes.js';
import { roundTo } from '@utils/numberFormatters.js';
import { mapState } from 'pinia';
import { useFacilitiesStore } from '@stores/facilities.ts';

export default {
    name: 'ProductCard',
    components: { ProductExpectedRunRates },
    emits: ['save', 'select', 'edit', 'cancel', 'delete'],
    props: {
        /**
         * Product fields.
         */
        name: {
            type: String,
            required: true
        },
        description: {
            type: String,
            required: true
        },
        unitMeasure: {
            type: String,
            required: true
        },
        unitPacking: {
            type: [Number, String],
            required: true
        },
        code: {
            type: String,
            required: true
        },
        expectedRunRates: {
            type: Array,
            required: false,
            default() {
                return [];
            }
        },
        packagingFactors: {
            type: Object,
            required: false,
            default() {
                return null;
            }
        },
        // array of sensors which can be edited, for example ['net', 'reject']
        editablePackagingFactors: {
            type: Array,
            required: false,
            default() {
                return [];
            }
        },
        /**
         * Card options.
         */
        // is the card currently selected?
        isSelected: {
            type: Boolean,
            required: false,
            default: false
        },
        // should cards be selectable?
        isSelectable: {
            type: Boolean,
            required: false,
            default: false
        },
        // inline edit mode
        inlineEditMode: {
            type: Boolean,
            required: false,
            default: false
        },
        // permissions
        canEdit: {
            type: Boolean,
            required: false,
            default: false
        },
        canDelete: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    watch: {
        expectedRunRates: {
            immediate: true,
            handler(value) {
                this.form.runRates = JSON.parse(JSON.stringify(value));
            }
        },
        /**
         * Initialize packaging factors when sensor packaging factors are changed.
         */
        packagingFactors(val) {
            this.form.packagingFactors = val;
        }
    },
    data() {
        return {
            id: null,
            form: {
                runRates: [],
                packagingFactors: this.packagingFactors
            },
            formRules: {
                expectedRunRate: [val => this.validateRunRate(val)],
                packagingFactors: [val => this.validatePackagingFactor(val)]
            }
        };
    },
    computed: {
        ...mapState(useFacilitiesStore, ['getSelectedFacility']),
        /**
         * Get facility.
         * @return {Object} - Facility object.
         */
        facility() {
            return this.getSelectedFacility;
        },
        /**
         * Convert unit packing and measure to facility unit system.
         * @return {Object} - Converted unit packing and measure.
         */
        convertedUnits() {
            const { value: packing, unit: measure } = convertToUnitSystem(
                parseFloat(this.unitPacking),
                this.unitMeasure,
                this.facility?.unit_system
            );
            return { packing, measure };
        },
        /**
         * Determine if all editable fields are valid.
         * @return {Boolean}
         */
        allExpectedRunRatesValid() {
            return this.form.runRates.some(
                runRate => isNaN(runRate) || parseFloat(runRate) < 0
            );
        }
    },
    methods: {
        roundTo,
        /**
         * Handle save when editing a card inline.
         */
        save() {
            // validate inputs
            if (this.allExpectedRunRatesValid) {
                const fields = {
                    id: this.id,
                    expectedRunRates: this.form.runRates,
                    packagingFactors: this.form.packagingFactors
                };
                this.$emit('save', fields);
            }
        },
        /**
         * Validate run rate input.
         * @param {String} value - User input from 'expected_run_rate' field.
         * @return {Boolean} - Valid inputs returns true.
         * @return {String} - Error message.
         */
        validateRunRate(value) {
            if (isNaN(value)) {
                return this.$t('Enter a positive real number');
            } else {
                // convert value to number
                value = parseFloat(value);
                if (value <= 0) {
                    return this.$t('Run rate must be positive');
                }
            }
        },
        /**
         * Validate packaging factors input.
         * @param {Number} value - User input from 'packaging factors' field.
         * @return {Boolean} - Valid inputs returns true.
         * @return {String} - Error message.
         */
        validatePackagingFactor(value) {
            if (value < 0) {
                return this.$t('Packaging factor must be positive');
            }
            return true;
        },
        /**
         * Get sensor label.
         * @param {String} sensorType - Sensor type.
         * @return {String} - Sensor label.
         */
        getSensorLabel(sensorType) {
            return SENSOR_TYPES[sensorType]['label'];
        }
    }
};
</script>

<style lang="scss">
.product-card--field {
    font-size: 17px;
}

.product-card__icons {
    color: $grey-7;
    font-weight: 500;
    display: flex;
    justify-content: flex-start;
}

.product-card__description {
    color: $grey-7;
}

.product-card--selectable {
    cursor: pointer;
}

.product-card--selected {
    background-color: $grey-3;
}
.product-card--input__lte_xs {
    padding-right: 10px;
    width: 100%;
}
.product-card--input__lte_lg {
    width: 250px;
}
.target-downtime {
    display: inline-block;
}
</style>
